import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  FiActivity,
  FiLoader,
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiClock,
  FiUser,
  FiShield
} from 'react-icons/fi';
import { secureSettingsAPI } from '../services/api';
import { useTranslation } from '../hooks/useTranslation';

const categoryColors = {
  ai: 'bg-purple-100 text-purple-700',
  blockchain: 'bg-blue-100 text-blue-700',
  geocoding: 'bg-green-100 text-green-700',
  api_keys: 'bg-red-100 text-red-700',
  settings: 'bg-yellow-100 text-yellow-700',
  auth: 'bg-indigo-100 text-indigo-700',
  challenges: 'bg-orange-100 text-orange-700',
  users: 'bg-teal-100 text-teal-700',
  admin: 'bg-pink-100 text-pink-700',
  app_config: 'bg-cyan-100 text-cyan-700'
};

const AuditLog = () => {
  const { t } = useTranslation();
  const [limit, setLimit] = useState(50);
  const [category, setCategory] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const { data, isLoading, error } = useQuery(
    ['audit-log', limit, category],
    async () => {
      const response = await secureSettingsAPI.getAuditLog(limit, category || null);
      return response.data;
    },
    { staleTime: 30000 }
  );

  const logs = data?.auditLog || data?.logs || data || [];

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-600 flex items-center gap-2">
          <FiActivity className="w-6 h-6" />
          {t('cms.activityLog.title')}
        </h1>
        <p className="text-gray-600">{t('cms.activityLog.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <FiFilter className="w-4 h-4 text-gray-500" />
            <label className="text-sm font-medium text-gray-700">Category:</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Categories</option>
              <option value="challenges">Challenges</option>
              <option value="users">Users</option>
              <option value="admin">Admin Management</option>
              <option value="settings">Settings</option>
              <option value="app_config">App Config</option>
              <option value="blockchain">Blockchain</option>
              <option value="api_keys">API Keys</option>
              <option value="ai">AI</option>
              <option value="geocoding">Geocoding</option>
              <option value="auth">Auth</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Show:</label>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={25}>25 entries</option>
              <option value={50}>50 entries</option>
              <option value={100}>100 entries</option>
            </select>
          </div>

          <div className="flex-1" />

          <span className="text-sm text-gray-500">
            {Array.isArray(logs) ? logs.length : 0} entries
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <FiLoader className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">{t('cms.common.loading')}</span>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">
            <FiShield className="w-8 h-8 mx-auto mb-2" />
            <p className="font-medium">Access Denied</p>
            <p className="text-sm text-gray-500 mt-1">This page requires master admin privileges.</p>
          </div>
        ) : !Array.isArray(logs) || logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FiActivity className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>{t('cms.activityLog.noLogs')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">{t('cms.activityLog.timestamp')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">{t('cms.activityLog.user')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">{t('cms.activityLog.action')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">{t('cms.activityLog.target')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">{t('cms.activityLog.details')}</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">{t('cms.activityLog.ipAddress')}</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => {
                  const isExpanded = expandedRow === idx;
                  const cat = log.category || 'unknown';
                  const colorClass = categoryColors[cat] || 'bg-gray-100 text-gray-700';

                  return (
                    <React.Fragment key={log.id || idx}>
                      <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-gray-50' : ''}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <FiClock className="w-3.5 h-3.5 text-gray-400" />
                            {formatTimestamp(log.timestamp || log.created_at)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <FiUser className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-medium text-gray-900">{log.admin_username || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-gray-900">{log.action || '-'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                            {cat}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-xs truncate text-gray-600">
                          {log.details
                            ? (typeof log.details === 'object' ? JSON.stringify(log.details) : log.details)
                            : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500 font-mono text-xs">
                          {log.ip_address || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => setExpandedRow(isExpanded ? null : idx)}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                            title={isExpanded ? 'Collapse' : 'Expand details'}
                          >
                            {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={7} className="px-4 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                              {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 && (
                                <div>
                                  <span className="font-medium text-gray-700 block mb-1">{t('cms.activityLog.details')}</span>
                                  <pre className="bg-white border border-gray-200 rounded p-2 text-xs text-gray-600 overflow-x-auto max-h-32 overflow-y-auto">
                                    {JSON.stringify(log.details, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log.old_value && (
                                <div>
                                  <span className="font-medium text-gray-700 block mb-1">Old Value</span>
                                  <pre className="bg-white border border-gray-200 rounded p-2 text-xs text-gray-600 overflow-x-auto max-h-32 overflow-y-auto">
                                    {typeof log.old_value === 'object' ? JSON.stringify(log.old_value, null, 2) : log.old_value}
                                  </pre>
                                </div>
                              )}
                              {log.new_value && (
                                <div>
                                  <span className="font-medium text-gray-700 block mb-1">New Value</span>
                                  <pre className="bg-white border border-gray-200 rounded p-2 text-xs text-gray-600 overflow-x-auto max-h-32 overflow-y-auto">
                                    {typeof log.new_value === 'object' ? JSON.stringify(log.new_value, null, 2) : log.new_value}
                                  </pre>
                                </div>
                              )}
                              {log.user_agent && (
                                <div>
                                  <span className="font-medium text-gray-700 block mb-1">User Agent</span>
                                  <p className="text-xs text-gray-500 break-all">{log.user_agent}</p>
                                </div>
                              )}
                              {!log.details && !log.old_value && !log.new_value && !log.user_agent && (
                                <div className="text-gray-400 text-sm">No additional details available</div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
